import type { ReactNode } from 'react';
import { memo } from 'react';

import { cn } from '@infinex/ui-styles';
import { Checkbox, Content, ContextualHelp } from '@infinex/ui/components';

import { AssetListItemContent } from './AssetListItemContent';
import { AssetListItemPressable } from './AssetListItemPressable';
import { TokenSourceWallets } from './TokenSourceWallets';

import type { WalletSourceMetadata } from '../../features/balances';
import { FormattedNumber } from '../formatted-number';

interface ChainAssetBalanceListItemProps {
  assetIcon: React.ReactNode;
  primaryTitle: string;
  secondaryTitle: ReactNode;

  /** USD value to display (shows with $ prefix). Ignored if balanceDisplay is provided. */
  usdValue?: number | null;
  /** Custom balance display element. Takes precedence over usdValue. */
  balanceDisplay?: ReactNode;
  /** Optional full-width row rendered below the main row (e.g. market stats) */
  footer?: ReactNode;
  sources?: WalletSourceMetadata[];
  showWalletSources?: boolean;
  isUnofficial?: boolean;
  /** If true, applies accent highlight to selected items */
  highlightSelected?: boolean;
  /** Whether this item is the currently selected asset */
  isSelected?: boolean;

  /** Whether this item is disabled */
  isDisabled?: boolean;
  disabledLabel?: string;
  disabledDetails?: string;

  testId?: string;
  /** Selection mode: single (click to select) or multi (checkbox toggle) */
  selectionMode?: 'single' | 'multi';
  /** Callback for single-select mode */
  onSelection: () => void;
  /** Callback for multi-select toggle */
  onToggle?: () => void;
  className?: string;
}

/**
 * Shared component to show both token and nft balances in the asset select list
 */
export const ChainAssetBalanceListItem = memo(
  ({
    sources = [],
    onSelection,
    primaryTitle,
    secondaryTitle,
    onToggle,
    selectionMode = 'single',
    highlightSelected = false,
    isSelected = false,
    testId,
    isDisabled,
    disabledLabel,
    disabledDetails,
    assetIcon,
    showWalletSources = true,
    usdValue,
    balanceDisplay,
    footer,
    isUnofficial = false,
    className,
  }: ChainAssetBalanceListItemProps) => {
    const checkbox = selectionMode === 'multi' && (
      <Checkbox
        isSelected={isSelected}
        isDisabled={isDisabled}
        onChange={onToggle}
        className="gap-0"
        aria-label={`Select ${primaryTitle}`}
      />
    );

    // Without a custom balanceDisplay, the USD value and wallet sources sit
    // at the end of their own text lines (line-end slots) so the title only
    // truncates once it nears the balance — a wide sources label can no
    // longer squeeze the whole left column.
    const usdEnd =
      usdValue != null && usdValue > 0 ? (
        <span
          className={cn(
            'text-emphasis leading-tight font-semibold',
            isDisabled && 'text-disabled'
          )}
        >
          $
          <FormattedNumber
            value={usdValue}
            decimalPartClassName="text-secondary"
          />
        </span>
      ) : undefined;

    const sourcesEnd =
      showWalletSources && sources.length > 0 ? (
        <div
          className={cn(
            'text-sm/tight font-medium',
            isDisabled ? 'text-disabled' : 'text-secondary'
          )}
        >
          <TokenSourceWallets
            sources={sources}
            isDisabled={isDisabled || false}
          />
        </div>
      ) : undefined;

    const textContent = (
      <AssetListItemContent
        primary={primaryTitle}
        secondary={secondaryTitle}
        isDisabled={isDisabled}
        isUnofficial={isUnofficial}
        primaryEnd={balanceDisplay == null ? usdEnd : undefined}
        secondaryEnd={balanceDisplay == null ? sourcesEnd : undefined}
      />
    );

    const rightSide = (
      <div className="flex">
        {balanceDisplay != null && (
          <div className="flex shrink-0 flex-col items-end gap-y-0.5">
            {balanceDisplay}
          </div>
        )}

        {/* Placeholder for ContextualHelp (positioned outside button to avoid nested buttons) */}
        {isDisabled && disabledLabel && disabledDetails && (
          <div className="size-6" />
        )}
      </div>
    );

    const mainRow =
      footer == null ? (
        <>
          <div
            className={cn(
              'flex w-full min-w-0 items-center gap-4',
              isDisabled && 'opacity-50 grayscale'
            )}
          >
            {checkbox}
            {assetIcon}
            {textContent}
          </div>

          {rightSide}
        </>
      ) : (
        // The footer spans from the text column to the card's right edge,
        // with the icon centered against the whole stack.
        <div
          className={cn(
            'flex w-full min-w-0 items-center gap-4',
            isDisabled && 'opacity-50 grayscale'
          )}
        >
          {checkbox}
          {assetIcon}
          <div className="flex min-w-0 flex-1 flex-col gap-y-2">
            <div className="flex w-full items-center justify-between gap-4">
              {textContent}
              {rightSide}
            </div>
            {footer}
          </div>
        </div>
      );

    return (
      <div className="relative flex w-full items-center rounded-xl">
        <AssetListItemPressable
          data-testid={testId}
          isDisabled={isDisabled}
          isSelected={isSelected}
          highlightSelected={highlightSelected}
          className={cn('relative', className)}
          onPress={() => {
            if (selectionMode === 'multi') {
              onToggle?.();
            } else {
              onSelection();
            }
          }}
        >
          {mainRow}
        </AssetListItemPressable>

        {/* Overlay positions ContextualHelp to match the placeholder inside the button */}
        {isDisabled && disabledLabel && disabledDetails && (
          <div
            className={
              'pointer-events-none absolute top-1/2 right-3 flex size-6 -translate-y-1/2 items-start justify-end'
            }
          >
            <ContextualHelp
              aria-label={disabledLabel}
              variant="info"
              size="base"
              placement="top end"
              className="pointer-events-auto"
            >
              <Content className="text-sm">{disabledDetails}</Content>
            </ContextualHelp>
          </div>
        )}
      </div>
    );
  }
);
